import styled, { keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   ExtendVideoModal — Light Water redesign
   Frosted-white shell, gradient title, brand-aqua circular header/footer
   buttons, light frost tooltip, secondary frost cancel pill. JS layer
   untouched. All original named exports preserved.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

// ─── Modal shell ────────────────────────────────────────────────────────────

export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-height: 90dvh;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  overflow: hidden;
  isolation: isolate;
  z-index: 1000;
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Header ─────────────────────────────────────────────────────────────────

export const ModalHeader = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 22px 22px 14px;
  flex-shrink: 0;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

/* Frosted-glass circle close button */
export const CloseButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-text-strong);
  }

  /* Top-left specular */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;

    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px);
  }
`;

// ─── Body ───────────────────────────────────────────────────────────────────

export const ModalBody = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 18px;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  gap: 16px;
`;

export const ContentContainer = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  min-height: 0;
  overflow-y: auto;
`;

export const Content = styled.div`
  display: flex;
  gap: 4px;
  flex: 1;
  height: fit-content;
`;

export const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  flex: 1;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow: var(--shadow-micro);

  h3 {
    margin: 0 0 8px;
    color: var(--color-text-strong);
    font-family: var(--font-family-primary);
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    color: var(--color-text-muted);
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 500;
    line-height: normal;
    max-width: 400px;
  }
`;

export const PaginationWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

// ─── Checkbox overlay (selection badge on collection items) ─────────────────

export const CheckboxContainer = styled.div`
  display: flex;
  padding: 4px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);

  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-600-rgb), 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);

  z-index: 30;
  pointer-events: auto;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Footer ─────────────────────────────────────────────────────────────────

export const Footer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 18px 18px;
  flex-shrink: 0;
  width: 100%;
  margin: 0 auto;
  max-width: 768px;
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

/* Frosted circle button — used for settings + info icon slots */
export const SettingsButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    var(--shadow-micro),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  cursor: pointer;
  outline: none;
  color: var(--color-brand-700);
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  svg {
    color: currentColor;
    path {
      stroke: currentColor;
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);
  }
`;

export const InfoButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    var(--shadow-micro),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  cursor: pointer;
  outline: none;
  color: var(--color-brand-700);
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  svg {
    color: currentColor;
    path {
      stroke: currentColor;
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);
  }
`;

export const TooltipContainer = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  right: 0;
  width: 340px;
  z-index: 100;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-3);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow:
    var(--shadow-panel),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);

  /* Tiny arrow pointing down */
  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    right: 22px;
    width: 12px;
    height: 12px;
    background: var(--color-surface-3);
    border-right: 1px solid var(--color-line);
    border-bottom: 1px solid var(--color-line);
    transform: rotate(45deg);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.55);
  }

  [data-theme="dark"] &::after {
    border-color: transparent;
  }

  @media (max-width: 480px) {
    width: 270px;
    right: -8px;

    &::after {
      right: 16px;
    }
  }
`;

export const TooltipText = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
`;

/* Secondary frost pill — sits below primary CTA row */
export const CancelButton = styled.button`
  display: flex;
  width: 100%;
  height: 42px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  outline: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
    color: var(--color-brand-700);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
